import { ActivationEntryType } from "app/pages/Customer/Activation/activation-entry-types";
import {
  normalizePlanDuration,
  type StandalonePlanDuration,
} from "./standalone-plan-resolution";
import { isFixedPhoneMultiLineCheckoutEnabled } from "./fixed-phone-purchase-config";

export const FIXED_PHONE_STORAGE_KEY = "fixedPhonePurchaseFlow";

/**
 * Marker matched against `phoneBaseProductStorageKey` in review/sync hooks so browse ids are read only from
 * `fixedPhoneCart.phoneBaseBrowseProductIds` inside `fixedPhonePurchaseFlow` (not from separate session keys).
 */
export const FIXED_PHONE_PHONE_BASE_PRODUCT_ID_KEY =
  "fixedPhonePhoneBaseProductId";

export const MAX_STANDALONE_PHONE_BASE_CART_QUANTITY = 5;

/** Effective cart cap — 1 when multi-line checkout is disabled. */
export function getStandaloneCartMaxQuantity(): number {
  return isFixedPhoneMultiLineCheckoutEnabled()
    ? MAX_STANDALONE_PHONE_BASE_CART_QUANTITY
    : 1;
}

export const FIXED_PHONE_CART_UPDATED_EVENT = "fixedPhoneCartUpdated";

export interface FixedPhoneCartLineItem {
  productId?: string;
  variantId?: string;
  planId?: string;
  storage?: string;
  condition?: string;
  color?: string;
  phoneBaseProductId?: string;
  lineId?: string;
  quantity?: number;
  productName?: string;
  conditionLabel?: string;
  planLabel?: string;
}

export type StandaloneBrowsePlanIntent = "promo" | "simple-pricing" | "retail";

export interface FixedPhoneCart {
  address?: string;
  zipCode?: string;
  planDurationHint?: string;
  /** Until phone-base applies the matching plan, forces duration over stale UI state. */
  pendingBrowsePlanIntent?: StandaloneBrowsePlanIntent;
  /**
   * In-progress PDP selection (plan, condition, etc.) before "Buy Now" commits to `items`.
   * @deprecated Prefer `productDrafts`; kept as mirror of the last edited browse product.
   */
  draft?: FixedPhoneCartLineItem;
  /** Per catalog browse id — prevents selections on one phone-base page affecting another. */
  productDrafts?: Record<string, FixedPhoneCartLineItem>;
  /**
   * Catalog ids of phone-base **pages** visited this session (deduped order). Used for reviews / profile-sync
   * eligibility, not as checkout identifiers — see `FIXED_PHONE_PHONE_BASE_PRODUCT_ID_KEY`.
   */
  phoneBaseBrowseProductIds?: string[];
  items: FixedPhoneCartLineItem[];
}

/**
 * Which activation entry path produced checkout/session data; only stored when `flowType === "activation"`.
 * `preloaded-sim` = mobile plan service flow (preloaded SIM / SIM upsell on `/activation`).
 * `fixed-phone-base` = reseller fixed-phone hardware activation (activation code flow).
 */
export type ActivationPurchaseFlow =
  (typeof ActivationEntryType)[keyof typeof ActivationEntryType];

export interface FixedPhoneOrderSummaryData {
  enteredCoupon?: string;
  appliedCoupon?: string;
  isCouponApplied?: boolean;
  couponAppliedByCustomer?: boolean;
}

export interface FixedPhoneCheckoutData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  securityQuestion?: string;
  answer?: string;
  streetAddress?: string;
  apt?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  source?: string;
  nameOnCredit?: string;
  cardNumber?: boolean;
  cardExpiry?: boolean;
  cardCvc?: boolean;
  acceptTerms?: boolean;
  trialOption?: "skip" | "subscribe";
  promoCode?: string;
  paymentMethodId?: string;
  selectedPaymentMethodType?: string;
  shippingMethod?: "standard" | "expedited";
}

export interface FixedPhonePurchaseFlowData {
  customerId?: string;
  checkout?: FixedPhoneCheckoutData;
  orderSummary?: FixedPhoneOrderSummaryData;
  flowType?: "activation" | "fixed";
  activationPurchaseFlow?: ActivationPurchaseFlow;
  activation?: ActivationFlowData;
  fixedPhoneCart?: FixedPhoneCart;
}

export interface ActivationFlowData {
  zipCode?: string;
  address?: string;
  activationCode?: string;
  monthlyPlanId?: string;
  yearlyPlanId?: string;
  planId?: string;
  selectedPlanType?: "monthly" | "yearly";
  optForSecondMonthUpsell?: boolean;
  optForSecurityPlan?: boolean;
  securityPlanId?: string;
  simData?: any;
  preloadedSimInventoryDevice?: any;
  preloadedSimDeviceForm?: {
    simNumber?: string;
    imeiNumber?: string;
  };
  preloadedSimVerifiedSimDigits?: string;
  preloadedSimVerifiedImei?: string;
  /** Set after device inventory success; used to detect edits that invalidate later steps. */
  preloadedSimDeviceCommitFingerprint?: string;
  preloadedSimPrimaryDataTier?: string;
  preloadedSimServiceFrequency?: string;
  preloadedSimPlanId?: string;
  preloadedSimOptedForDataOrFrequencyUpsell?: boolean;
  preloadedSimUpgradeCardDraft?: {
    primaryDataTier: string;
    serviceFrequency: string;
  };
}

export const getFixedPhonePurchaseFlowData =
  (): FixedPhonePurchaseFlowData | null => {
    try {
      const data = sessionStorage.getItem(FIXED_PHONE_STORAGE_KEY);
      if (!data) return null;
      return JSON.parse(data) as FixedPhonePurchaseFlowData;
    } catch (error) {
      console.error("Error reading fixed phone purchase flow data:", error);
      return null;
    }
  };

export const saveFixedPhonePurchaseFlowData = (
  data: Partial<FixedPhonePurchaseFlowData>
): void => {
  try {
    const existing = getFixedPhonePurchaseFlowData() || {};
    const updated = { ...existing };

    // Perform a shallow merge on known nested objects to prevent data loss when saving independently
    if (data.checkout !== undefined) {
      updated.checkout = { ...(existing.checkout || {}), ...data.checkout };
    }
    if (data.orderSummary !== undefined) {
      updated.orderSummary = {
        ...(existing.orderSummary || {}),
        ...data.orderSummary,
      };
    }
    if (data.flowType !== undefined) {
      updated.flowType = data.flowType;
    }
    if (data.activationPurchaseFlow !== undefined) {
      updated.activationPurchaseFlow = data.activationPurchaseFlow;
    }
    if (data.activation !== undefined) {
      updated.activation = { ...(existing.activation || {}), ...data.activation };
    }
    if (data.customerId !== undefined) {
      updated.customerId = data.customerId;
    }
    if (data.fixedPhoneCart !== undefined) {
      const prev = existing.fixedPhoneCart || { items: [] };
      const inc = data.fixedPhoneCart;
      updated.fixedPhoneCart = { ...prev, ...inc };
      if (inc.items === undefined) {
        updated.fixedPhoneCart.items = Array.isArray(prev.items)
          ? prev.items
          : [];
      }
      if ("draft" in inc) {
        if (inc.draft === undefined || inc.draft === null) {
          delete updated.fixedPhoneCart.draft;
        } else {
          updated.fixedPhoneCart.draft = {
            ...(prev.draft || {}),
            ...inc.draft,
          };
        }
      }
      if ("productDrafts" in inc) {
        if (inc.productDrafts === undefined || inc.productDrafts === null) {
          delete updated.fixedPhoneCart.productDrafts;
        } else {
          updated.fixedPhoneCart.productDrafts = {
            ...(prev.productDrafts || {}),
            ...inc.productDrafts,
          };
        }
      }
      (
        [
          "address",
          "zipCode",
          "planDurationHint",
          "pendingBrowsePlanIntent",
          "phoneBaseBrowseProductIds",
        ] as const
      ).forEach((k) => {
        if (!(k in inc)) return;
        const v = inc[k];
        if (v === undefined) {
          delete updated.fixedPhoneCart![k];
          return;
        }
        if (
          (k === "address" || k === "zipCode" || k === "planDurationHint") &&
          typeof v === "string" &&
          trimStr(v) === ""
        ) {
          delete updated.fixedPhoneCart![k];
        }
      });
    }
    if (updated.flowType === "fixed") {
      delete updated.activationPurchaseFlow;
    }
    sessionStorage.setItem(FIXED_PHONE_STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving fixed phone purchase flow data:", error);
  }
};

/** Router `state` for `/checkout` when coming from activation steps; includes purchase-flow when set in session. */
export function buildActivationCheckoutLocationState(): {
  flowType: "activation";
  from: "activation";
  activationPurchaseFlow?: ActivationPurchaseFlow;
} {
  const full = getFixedPhonePurchaseFlowData();
  const flow = full?.activationPurchaseFlow;
  return {
    flowType: "activation",
    from: "activation",
    ...(flow ? { activationPurchaseFlow: flow } : {}),
  };
}

export const getActivationFlowData = (): ActivationFlowData | null => {
  const data = getFixedPhonePurchaseFlowData();
  if (!data || data.flowType !== "activation") {
    return null;
  }
  return data.activation || null;
};

export const saveActivationFlowData = (
  data: Partial<ActivationFlowData>,
  opts?: { activationPurchaseFlow?: ActivationPurchaseFlow }
): void => {
  saveFixedPhonePurchaseFlowData({
    flowType: "activation",
    activation: data,
    ...(opts?.activationPurchaseFlow !== undefined
      ? { activationPurchaseFlow: opts.activationPurchaseFlow }
      : {}),
  });
};

export const resetActivationFlowProgress = (): void => {
  try {
    const full = getFixedPhonePurchaseFlowData();
    const existingActivation = full?.activation || {};
    const flow = full?.activationPurchaseFlow;
    const fixedPhoneCart = full?.fixedPhoneCart;
    const next: FixedPhonePurchaseFlowData = {
      flowType: "activation",
      activation: {
        zipCode: existingActivation.zipCode,
        address: existingActivation.address,
      },
      ...(flow ? { activationPurchaseFlow: flow } : {}),
      ...(fixedPhoneCart ? { fixedPhoneCart } : {}),
    };
    sessionStorage.setItem(FIXED_PHONE_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    console.error("Error resetting activation flow progress:", error);
  }
};

export const getFixedPhoneCheckoutData = (): FixedPhoneCheckoutData | null => {
  const data = getFixedPhonePurchaseFlowData();
  return data?.checkout || null;
};

export const saveFixedPhoneCheckoutData = (
  data: FixedPhoneCheckoutData
): void => {
  saveFixedPhonePurchaseFlowData({ checkout: data });
};

export const clearFixedPhoneCheckoutData = (): void => {
  const data = getFixedPhonePurchaseFlowData();
  if (data) {
    delete data.checkout;
    sessionStorage.setItem(FIXED_PHONE_STORAGE_KEY, JSON.stringify(data));
  }
};

export const clearFixedPhonePurchaseFlow = (): void => {
  try {
    sessionStorage.removeItem(FIXED_PHONE_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing fixed phone purchase flow:", error);
  }
};

export const getPersistedFixedPhoneCustomerId = (): string | null => {
  const data = getFixedPhonePurchaseFlowData();
  if (!data?.customerId || typeof data.customerId !== "string") {
    return null;
  }
  const id = data.customerId.trim();
  return id || null;
};

export const savePersistedFixedPhoneCustomerId = (
  customerId?: string | null
): void => {
  const normalized = typeof customerId === "string" ? customerId.trim() : "";
  if (!normalized) return;
  saveFixedPhonePurchaseFlowData({ customerId: normalized });
};

export const getFixedPhoneOrderSummaryData =
  (): FixedPhoneOrderSummaryData | null => {
    const data = getFixedPhonePurchaseFlowData();
    return data?.orderSummary || null;
  };

export const saveFixedPhoneOrderSummaryData = (
  orderSummary: Partial<FixedPhoneOrderSummaryData>
): void => {
  saveFixedPhonePurchaseFlowData({ orderSummary });
};

function trimStr(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function getStandaloneCoverageMerged(): {
  address: string;
  zipCode: string;
} {
  const cart = getFixedPhonePurchaseFlowData()?.fixedPhoneCart;
  return {
    address: trimStr(cart?.address),
    zipCode: trimStr(cart?.zipCode),
  };
}

function dispatchFixedPhoneCartUpdated(): void {
  try {
    window.dispatchEvent(new CustomEvent(FIXED_PHONE_CART_UPDATED_EVENT));
  } catch {
    // Non-browser environments
  }
}

function generateCartLineId(): string {
  return `fp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeCartLineQuantity(
  item: FixedPhoneCartLineItem,
): FixedPhoneCartLineItem {
  const quantity = item.quantity;
  return {
    ...item,
    quantity:
      typeof quantity === "number" && quantity > 0
        ? Math.floor(quantity)
        : 1,
  };
}

function isCommittedCartLine(item: FixedPhoneCartLineItem | undefined): boolean {
  if (!item) return false;
  return !!(trimStr(item.productId) && trimStr(item.variantId));
}

export function getCartLineIdentityKey(
  item: Partial<FixedPhoneCartLineItem>,
): string {
  return [
    trimStr(item.productId),
    trimStr(item.variantId),
    trimStr(item.planId),
    trimStr(item.storage),
    trimStr(item.condition),
    trimStr(item.color),
  ].join("|");
}

function cloneProductDrafts(
  cart: FixedPhoneCart | undefined,
): Record<string, FixedPhoneCartLineItem> {
  if (!cart?.productDrafts) return {};
  return { ...cart.productDrafts };
}

/** In-progress PDP selections scoped to a single phone-base browse product id. */
export function getStandaloneDraftForProduct(
  phoneBaseProductId?: string,
): FixedPhoneCartLineItem {
  const id = trimStr(phoneBaseProductId);
  if (!id) {
    const legacy = getFixedPhonePurchaseFlowData()?.fixedPhoneCart?.draft;
    return legacy ? { ...legacy } : {};
  }

  const cart = getFixedPhonePurchaseFlowData()?.fixedPhoneCart;
  if (!cart) return { phoneBaseProductId: id };

  const fromMap = cart.productDrafts?.[id];
  if (fromMap) return { ...fromMap };

  const legacy = cart.draft;
  if (legacy && trimStr(legacy.phoneBaseProductId) === id) {
    return { ...legacy };
  }

  return { phoneBaseProductId: id };
}

/** @deprecated Use `getStandaloneDraftForProduct(phoneBaseProductId)`. */
export function getStandaloneDraftMerged(
  phoneBaseProductId?: string,
): FixedPhoneCartLineItem {
  return getStandaloneDraftForProduct(phoneBaseProductId);
}

export function patchStandaloneDraftForProduct(
  phoneBaseProductId: string,
  partial: Partial<FixedPhoneCartLineItem>,
): void {
  const id = trimStr(phoneBaseProductId);
  if (!id) return;

  const cleaned = cleanCartLinePartial({ ...partial, phoneBaseProductId: id });
  if (Object.keys(cleaned).length === 0) return;

  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  const productDrafts = cloneProductDrafts(cart);
  const merged = { ...(productDrafts[id] || {}), ...cleaned };
  productDrafts[id] = merged;

  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: Array.isArray(cart.items) ? cart.items : [],
      productDrafts,
      draft: merged,
    },
  });
  dispatchFixedPhoneCartUpdated();
}

export function stripStandaloneDraftFieldsForProduct(
  phoneBaseProductId: string,
  keys: (keyof FixedPhoneCartLineItem)[],
): void {
  const id = trimStr(phoneBaseProductId);
  if (!id || !keys.length) return;

  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;

  const cart = existing.fixedPhoneCart;
  const productDrafts = cloneProductDrafts(cart);
  let activeDraft = cart.draft ? { ...cart.draft } : undefined;

  if (productDrafts[id]) {
    const row = { ...productDrafts[id] };
    keys.forEach((k) => {
      delete row[k];
    });
    productDrafts[id] = row;
    if (trimStr(activeDraft?.phoneBaseProductId) === id) {
      activeDraft = { ...row };
    }
  } else if (activeDraft && trimStr(activeDraft.phoneBaseProductId) === id) {
    keys.forEach((k) => {
      delete activeDraft![k];
    });
  }

  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: Array.isArray(cart.items) ? cart.items : [],
      productDrafts,
      ...(activeDraft ? { draft: activeDraft } : {}),
    },
  });
  dispatchFixedPhoneCartUpdated();
}

export function stripAllStandaloneProductDraftFields(
  keys: (keyof FixedPhoneCartLineItem)[],
): void {
  if (!keys.length) return;

  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;

  const cart = existing.fixedPhoneCart;
  const productDrafts = cloneProductDrafts(cart);

  Object.keys(productDrafts).forEach((productId) => {
    const row = { ...productDrafts[productId] };
    keys.forEach((k) => {
      delete row[k];
    });
    productDrafts[productId] = row;
  });

  let draft = cart.draft ? { ...cart.draft } : undefined;
  if (draft) {
    keys.forEach((k) => {
      delete draft![k];
    });
  }

  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: Array.isArray(cart.items) ? cart.items : [],
      productDrafts,
      ...(draft ? { draft } : {}),
    },
  });
  dispatchFixedPhoneCartUpdated();
}

export function getStandaloneCartLines(): FixedPhoneCartLineItem[] {
  const items = getFixedPhonePurchaseFlowData()?.fixedPhoneCart?.items ?? [];
  let needsPersist = false;
  const normalized = items
    .filter(isCommittedCartLine)
    .map((line) => {
      const withQty = normalizeCartLineQuantity(line);
      if (withQty.lineId) return withQty;
      needsPersist = true;
      return { ...withQty, lineId: generateCartLineId() };
    });

  if (needsPersist) {
    const existing = getFixedPhonePurchaseFlowData() || {};
    const cart = existing.fixedPhoneCart || { items: [] };
    saveFixedPhonePurchaseFlowData({
      fixedPhoneCart: { ...cart, items: normalized },
    });
  }

  return normalized;
}

export function getStandaloneCartTotalQuantity(): number {
  return getStandaloneCartLines().reduce(
    (sum, line) => sum + (line.quantity ?? 1),
    0,
  );
}

export function getPrimaryStandaloneCartItemMerged(): FixedPhoneCartLineItem {
  const item = getStandaloneCartLines()[0];
  return item ? { ...item } : {};
}

export function findStandaloneCartLineByBrowseProductId(
  phoneBaseProductId: string,
): FixedPhoneCartLineItem | undefined {
  const id = trimStr(phoneBaseProductId);
  if (!id) return undefined;
  return getStandaloneCartLines().find(
    (line) => trimStr(line.phoneBaseProductId) === id,
  );
}

export function findStandaloneCartLineByIdentity(
  partial: Partial<FixedPhoneCartLineItem>,
): FixedPhoneCartLineItem | undefined {
  const key = getCartLineIdentityKey(partial);
  if (!key.replace(/\|/g, "")) return undefined;
  return getStandaloneCartLines().find(
    (line) => getCartLineIdentityKey(line) === key,
  );
}

export function getStandaloneCartLineQuantity(
  partial: Partial<FixedPhoneCartLineItem>,
): number {
  return findStandaloneCartLineByIdentity(partial)?.quantity ?? 0;
}

export type StandaloneProductCartScope = {
  phoneBaseProductId?: string;
  productId?: string;
};

function lineMatchesProductScope(
  line: FixedPhoneCartLineItem,
  scope: StandaloneProductCartScope,
): boolean {
  const browseId = trimStr(scope.phoneBaseProductId);
  const productId = trimStr(scope.productId);
  if (browseId && trimStr(line.phoneBaseProductId) === browseId) return true;
  if (productId && trimStr(line.productId) === productId) return true;
  return false;
}

export function getStandaloneCartLinesForProduct(
  scope: StandaloneProductCartScope,
): FixedPhoneCartLineItem[] {
  if (!trimStr(scope.phoneBaseProductId) && !trimStr(scope.productId)) {
    return [];
  }
  return getStandaloneCartLines().filter((line) =>
    lineMatchesProductScope(line, scope),
  );
}

/** Total quantity for a catalog product across all variant/plan lines in cart. */
export function getStandaloneProductCartQuantity(
  scope: StandaloneProductCartScope,
): number {
  return getStandaloneCartLinesForProduct(scope).reduce(
    (sum, line) => sum + (line.quantity ?? 1),
    0,
  );
}

export function decrementStandaloneProductCart(
  scope: StandaloneProductCartScope,
  preferredIdentity?: Partial<FixedPhoneCartLineItem>,
): void {
  const lines = getStandaloneCartLinesForProduct(scope);
  if (!lines.length) return;

  const preferred =
    preferredIdentity &&
    findStandaloneCartLineByIdentity(preferredIdentity);
  const target =
    preferred && lineMatchesProductScope(preferred, scope)
      ? preferred
      : lines[lines.length - 1];

  if (!target?.lineId) return;
  if ((target.quantity ?? 1) <= 1) {
    removeStandaloneCartLine(target.lineId);
  } else {
    updateStandaloneCartLineQuantity(target.lineId, -1);
  }
}

export const PROMO_BANNER_PLAN_DURATION = "yearly" as const;
export const RETAIL_DEFAULT_PLAN_DURATION = "monthly" as const;

/** True when the URL includes `reset_ts` from the header promo banner. */
export function isPromoBannerResetUrl(search?: string): boolean {
  try {
    const query =
      search ?? (typeof window !== "undefined" ? window.location.search : "");
    return new URLSearchParams(query).has("reset_ts");
  } catch {
    return false;
  }
}

/**
 * Persists yearly promo intent before navigation so plan pickers never default to monthly
 * while waiting for the product API (common on `/fixed-phone` → phone-base on dev).
 */
export function getStandalonePendingBrowsePlanIntent(): StandaloneBrowsePlanIntent | "" {
  const raw =
    getFixedPhonePurchaseFlowData()?.fixedPhoneCart?.pendingBrowsePlanIntent;
  return raw === "promo" || raw === "simple-pricing" || raw === "retail"
    ? raw
    : "";
}

export function patchStandalonePendingBrowsePlanIntent(
  intent: StandaloneBrowsePlanIntent | undefined,
): void {
  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: Array.isArray(cart.items) ? cart.items : [],
      pendingBrowsePlanIntent: intent,
    },
  });
}

export function clearStandalonePendingBrowsePlanIntent(): void {
  patchStandalonePendingBrowsePlanIntent(undefined);
}

export function applyPromoBannerPlanIntent(phoneBaseProductId?: string): void {
  patchStandalonePlanDurationHint(PROMO_BANNER_PLAN_DURATION);
  patchStandalonePendingBrowsePlanIntent("promo");
  const id = trimStr(phoneBaseProductId);
  if (id) {
    stripStandaloneDraftFieldsForProduct(id, [
      "planId",
      "condition",
      "storage",
      "color",
    ]);
    return;
  }
  stripAllStandaloneProductDraftFields([
    "planId",
    "condition",
    "storage",
    "color",
  ]);
}

/** Hero / ready-to-upgrade / default list browse → New device + monthly plan on phone-base. */
export function applyRetailListBrowseDefaults(): void {
  patchStandalonePlanDurationHint(RETAIL_DEFAULT_PLAN_DURATION);
  patchStandalonePendingBrowsePlanIntent("retail");
  stripAllStandaloneProductDraftFields([
    "planId",
    "condition",
    "storage",
    "color",
  ]);
}

/** Simple-pricing CTA → New device + monthly or yearly per selected card. */
export function applySimplePricingBrowseIntent(duration: string): void {
  const normalized = normalizePlanDuration(duration);
  if (!normalized) return;
  patchStandalonePlanDurationHint(normalized);
  patchStandalonePendingBrowsePlanIntent("simple-pricing");
  stripAllStandaloneProductDraftFields(["planId", "condition"]);
}

export function getStandalonePlanDurationHint(): StandalonePlanDuration | "" {
  return normalizePlanDuration(
    getFixedPhonePurchaseFlowData()?.fixedPhoneCart?.planDurationHint,
  );
}

export function patchStandalonePlanDurationHint(durationHint: string): void {
  const hint = normalizePlanDuration(durationHint);
  if (!hint) return;
  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      planDurationHint: hint,
      items: Array.isArray(cart.items) ? cart.items : [],
    },
  });
}

export function clearStandalonePlanDurationHint(): void {
  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart?.planDurationHint) return;
  const cart = existing.fixedPhoneCart;
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: Array.isArray(cart.items) ? cart.items : [],
      planDurationHint: undefined,
    },
  });
}

export function stripPrimaryStandaloneCartFields(
  keys: (keyof FixedPhoneCartLineItem)[],
): void {
  if (!keys.length) return;
  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;
  const cart = existing.fixedPhoneCart;
  const items = Array.isArray(cart.items) ? [...cart.items] : [];
  if (items.length > 0) {
    const row = { ...items[0] };
    keys.forEach((k) => {
      delete row[k];
    });
    items[0] = row;
  }
  stripAllStandaloneProductDraftFields(keys);

  const afterStrip = getFixedPhonePurchaseFlowData()?.fixedPhoneCart;
  if (!afterStrip) return;

  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...afterStrip,
      items,
    },
  });
}

export function stripStandaloneCoverageFields(
  keys: ("zipCode" | "address")[],
): void {
  if (!keys.length) return;
  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;
  const prev = existing.fixedPhoneCart;
  const inc: FixedPhoneCart = {
    ...prev,
    items: Array.isArray(prev.items) ? prev.items : [],
  };
  keys.forEach((k) => {
    inc[k] = undefined;
  });
  saveFixedPhonePurchaseFlowData({ fixedPhoneCart: inc });
}

export function patchStandaloneCoverage(
  partial: Partial<Pick<FixedPhoneCart, "address" | "zipCode">>,
): void {
  const existing = getFixedPhonePurchaseFlowData() || {};
  const prev = existing.fixedPhoneCart || { items: [] };
  const inc: FixedPhoneCart = {
    ...prev,
    items: Array.isArray(prev.items) ? prev.items : [],
  };
  if (partial.address !== undefined) {
    const a = trimStr(partial.address);
    inc.address = a ? a : undefined;
  }
  if (partial.zipCode !== undefined) {
    const z = trimStr(partial.zipCode);
    inc.zipCode = z ? z : undefined;
  }
  saveFixedPhonePurchaseFlowData({ fixedPhoneCart: inc });
}

function cleanCartLinePartial(
  partial: Partial<FixedPhoneCartLineItem>,
): Partial<FixedPhoneCartLineItem> {
  const cleaned: Partial<FixedPhoneCartLineItem> = {};
  (
    Object.entries(partial) as [
      keyof FixedPhoneCartLineItem,
      string | number | undefined,
    ][]
  ).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (k === "quantity") {
      const qty = typeof v === "number" ? v : Number(v);
      if (Number.isFinite(qty) && qty > 0) {
        cleaned.quantity = Math.floor(qty);
      }
      return;
    }
    const t = trimStr(v);
    if (!t) return;
    cleaned[k] = t;
  });
  return cleaned;
}

export function patchStandaloneDraft(
  partial: Partial<FixedPhoneCartLineItem>,
): void {
  const cleaned = cleanCartLinePartial(partial);
  if (Object.keys(cleaned).length === 0) return;

  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: Array.isArray(cart.items) ? cart.items : [],
      draft: { ...(cart.draft || {}), ...cleaned },
    },
  });
  dispatchFixedPhoneCartUpdated();
}

/** Persists in-progress PDP selections (does not mutate committed cart lines). */
export function patchPrimaryStandaloneCartItem(
  partial: Partial<FixedPhoneCartLineItem>,
): void {
  const browseId = trimStr(partial.phoneBaseProductId);
  if (browseId) {
    patchStandaloneDraftForProduct(browseId, partial);
    return;
  }
  patchStandaloneDraft(partial);
}

export type AddToStandaloneCartResult =
  | { success: true }
  | { success: false; reason: "max_reached" | "invalid_line" };

export function addToStandaloneCart(
  partial: Partial<FixedPhoneCartLineItem>,
): AddToStandaloneCartResult {
  const cleaned = cleanCartLinePartial(partial);
  if (!cleaned.productId || !cleaned.variantId || !cleaned.planId) {
    return { success: false, reason: "invalid_line" };
  }

  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  const cartMaxQuantity = getStandaloneCartMaxQuantity();

  if (!isFixedPhoneMultiLineCheckoutEnabled()) {
    const nextItems = [
      normalizeCartLineQuantity({
        ...cleaned,
        lineId: generateCartLineId(),
        quantity: 1,
      }),
    ];
    saveFixedPhonePurchaseFlowData({
      fixedPhoneCart: {
        ...cart,
        items: nextItems,
      },
    });
    dispatchFixedPhoneCartUpdated();
    return { success: true };
  }

  const items = getStandaloneCartLines();
  const identityKey = getCartLineIdentityKey(cleaned);
  const totalQty = getStandaloneCartTotalQuantity();

  const existingIndex = items.findIndex(
    (line) => getCartLineIdentityKey(line) === identityKey,
  );

  let nextItems: FixedPhoneCartLineItem[];

  if (existingIndex >= 0) {
    if (totalQty >= cartMaxQuantity) {
      return { success: false, reason: "max_reached" };
    }
    nextItems = items.map((line, index) => {
      if (index !== existingIndex) return line;
      return normalizeCartLineQuantity({
        ...line,
        ...cleaned,
        quantity: (line.quantity ?? 1) + 1,
      });
    });
  } else {
    if (totalQty >= cartMaxQuantity) {
      return { success: false, reason: "max_reached" };
    }
    nextItems = [
      ...items,
      normalizeCartLineQuantity({
        ...cleaned,
        lineId: generateCartLineId(),
        quantity: 1,
      }),
    ];
  }

  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: nextItems,
    },
  });
  dispatchFixedPhoneCartUpdated();
  return { success: true };
}

export function updateStandaloneCartLineQuantity(
  lineId: string,
  delta: number,
): AddToStandaloneCartResult {
  const id = trimStr(lineId);
  if (!id || !delta) return { success: false, reason: "invalid_line" };

  const items = getStandaloneCartLines();
  const index = items.findIndex((line) => trimStr(line.lineId) === id);
  if (index < 0) return { success: false, reason: "invalid_line" };

  const current = items[index];
  const currentQty = current.quantity ?? 1;
  const nextQty = currentQty + delta;
  const totalQty = getStandaloneCartTotalQuantity();
  const cartMaxQuantity = getStandaloneCartMaxQuantity();

  if (delta > 0 && totalQty >= cartMaxQuantity) {
    return { success: false, reason: "max_reached" };
  }

  let nextItems: FixedPhoneCartLineItem[];
  if (nextQty <= 0) {
    nextItems = items.filter((_, i) => i !== index);
  } else {
    nextItems = items.map((line, i) =>
      i === index ? { ...line, quantity: nextQty } : line,
    );
  }

  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: { ...cart, items: nextItems },
  });
  dispatchFixedPhoneCartUpdated();
  return { success: true };
}

export function removeStandaloneCartLine(lineId: string): void {
  const id = trimStr(lineId);
  if (!id) return;
  const nextItems = getStandaloneCartLines().filter(
    (line) => trimStr(line.lineId) !== id,
  );
  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: { ...cart, items: nextItems },
  });
  dispatchFixedPhoneCartUpdated();
}

/**
 * Ensures checkout APIs read the first committed cart line.
 * Future multi-item checkout can replace this with aggregated payload logic.
 */
export function syncPrimaryCheckoutFromCart(): void {
  const lines = getStandaloneCartLines();
  if (lines.length === 0) return;

  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: lines,
    },
  });
}

/**
 * Commits the current PDP selection as the sole checkout line and syncs cart state.
 * When multi-line checkout is disabled, cart is always replaced with quantity 1.
 */
export function beginStandaloneCheckoutFromSelection(
  partial: Partial<FixedPhoneCartLineItem>,
): AddToStandaloneCartResult {
  const result = addToStandaloneCart(partial);
  if (!result.success) return result;

  if (partial.phoneBaseProductId) {
    patchPrimaryStandaloneCartItem({
      ...partial,
      phoneBaseProductId: partial.phoneBaseProductId,
    });
  }
  syncPrimaryCheckoutFromCart();
  return result;
}

export function pushStandalonePhoneBaseBrowseProductId(productId: string): void {
  const id = trimStr(productId);
  if (!id) return;
  const existing = getFixedPhonePurchaseFlowData() || {};
  const cart = existing.fixedPhoneCart || { items: [] };
  const prev = cart.phoneBaseBrowseProductIds?.length
    ? cart.phoneBaseBrowseProductIds
    : [];
  const next = prev.includes(id) ? prev : [...prev, id];
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      phoneBaseBrowseProductIds: next,
      items: Array.isArray(cart.items) ? cart.items : [],
    },
  });
}

export function clearStandalonePhoneBaseBrowseProductIds(): void {
  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;
  const prev = existing.fixedPhoneCart;
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...prev,
      items: Array.isArray(prev.items) ? prev.items : [],
      phoneBaseBrowseProductIds: undefined,
    },
  });
}

export function getStandalonePhoneBaseBrowseProductIds(): string[] {
  const ids =
    getFixedPhonePurchaseFlowData()?.fixedPhoneCart?.phoneBaseBrowseProductIds;
  return ids?.length ? [...ids] : [];
}

export function getFixedPhoneCartSummaryPayload(): {
  productId: string;
  variantId: string;
  planId: string;
  zipCode: string;
} | null {
  const m = getPrimaryStandaloneCartItemMerged();
  const productId = trimStr(m.productId);
  const variantId = trimStr(m.variantId);
  const planId = trimStr(m.planId);
  const zipCode = trimStr(getStandaloneCoverageMerged().zipCode);
  if (!productId || !variantId || !planId || !zipCode) return null;
  return { productId, variantId, planId, zipCode };
}

export function getStandaloneBillingZipFallback(): string {
  return trimStr(getStandaloneCoverageMerged().zipCode);
}

export function clearPrimaryStandaloneDeviceFields(): void {
  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;

  const cart = existing.fixedPhoneCart;
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: [],
      draft: undefined,
      productDrafts: undefined,
    },
  });
  dispatchFixedPhoneCartUpdated();
}

export function clearPrimaryStandaloneCartDeviceOnly(): void {
  const existing = getFixedPhonePurchaseFlowData();
  if (!existing?.fixedPhoneCart) return;

  const cart = existing.fixedPhoneCart;
  saveFixedPhonePurchaseFlowData({
    fixedPhoneCart: {
      ...cart,
      items: [],
      draft: undefined,
      productDrafts: undefined,
    },
  });
  dispatchFixedPhoneCartUpdated();
}

export function clearStandaloneCart(): void {
  clearPrimaryStandaloneDeviceFields();
}
